<template>
  <v-card flat class="my-0">
    <v-card-text>
      <v-col align-self="center">
        <v-row align="center">
          <v-select
            v-model="field.dynamicTableId"
            v-bind:label="$t('Table')"
            item-text="text"
            item-value="id"
            :items="internalTables"
            :error-messages="dynamicTableIdErrors"
            @input="validator.field.dynamicTableId.$touch()"
            @blur="validator.field.dynamicTableId.$touch()"
            @change="selectedTableChanged"
          >
            <template slot="append-outer">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    color="success"
                    icon
                    @click="onInternalTableButtonClick"
                  >
                    <v-icon>mdi-table-plus</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("InsertData") }}</span>
              </v-tooltip>
            </template>
          </v-select>
        </v-row>
        <v-row align="center">
          <v-radio-group :row="true" v-model="basicMode">
            <v-radio
              v-bind:label="$t('BasicMode')"
              :value="true"
              @change="generateCommand"
            ></v-radio>
            <v-radio
              v-bind:label="$t('AdvancedMode')"
              :value="false"
            ></v-radio>
          </v-radio-group>
        </v-row>
        <div v-if="basicMode">
          <v-row align="center">
            <v-select
              v-bind:label="$t('DisplayColumn')"
              multiple
              v-model="field.internalTable.displayColumns"
              :items="columns"
              @change="generateCommand"
            ></v-select>
          </v-row>
          <v-row
            v-for="(filter, index) in field.internalTable.filters"
            :key="index"
            align="center"
            justify="center"
          >
            <v-col cols="1">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    color="error"
                    icon
                    @click="removeFilter(index)"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("Remove") }}</span>
              </v-tooltip>
            </v-col>
            <v-col>
              <v-select
                clearable
                v-bind:label="$t('Filter')"
                v-model="filter.filterColumn"
                :items="columns"
                @change="generateCommand"
              ></v-select>
            </v-col>
            <v-col>
              <v-select
                clearable
                v-bind:label="$t('Value')"
                item-text="code"
                item-value="code"
                v-model="filter.filterValue"
                :items="contentTypeFields"
                @change="generateCommand"
              ></v-select>
            </v-col>
            <v-col cols="1">
              <v-tooltip top v-if="showAddFilter(index)">
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    color="primary"
                    icon
                    @click="addFilter"
                  >
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("Add") }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
          <v-row align="center">
            <v-select
              clearable
              v-bind:label="$t('SortBy')"
              :items="columns"
              v-model="field.internalTable.sortColumn"
              @change="generateCommand"
            ></v-select>
          </v-row>
        </div>
        <v-tabs v-if="!basicMode" v-model="selectedTab">
          <v-tab>{{ $t("Query") }}</v-tab>
          <v-tab>{{ $t("Site.ContentType.InternalExternalTablesFields.FastResultsQuery") }}</v-tab>
          <v-tab>{{ $t("NoResultQuery") }}</v-tab>
          <v-tab>{{ $t("Site.ContentType.InternalExternalTablesFields.NoResultPerformanceQuery") }}</v-tab>
          <v-tab-item>
            <div class="sql-editor">
              <codemirror v-model="field.command" :options="cmOption" @input="queryHasChanged" />
            </div>
          </v-tab-item>
          <v-tab-item>
            <div class="sql-editor">
              <codemirror v-model="field.performanceQuery" :options="cmOption" @input="queryHasChanged" />
            </div>
          </v-tab-item>
          <v-tab-item>
            <div class="sql-editor">
              <codemirror v-model="field.noResultQuery" :options="cmOption" @input="queryHasChanged" />
            </div>
          </v-tab-item>
          <v-tab-item>
             <div class="sql-editor">
               <codemirror v-model="field.noResultPerformanceQuery" :options="cmOption" @input="queryHasChanged" />
            </div>
          </v-tab-item>
        </v-tabs>
        <v-row class="align-center">
          <v-col cols="3" v-if="!basicMode && (selectedTab == 1 || selectedTab == 3)">
            <v-select
                v-model="selectedFilter"
                :items="filters"
                :label="$t('Filter')"
                item-text="text"
                item-value="id"
            ></v-select>
          </v-col>
          <v-col v-if="!basicMode && (selectedTab == 1 || selectedTab == 3)" cols="1">
            <v-btn @click="addPerformanceQueryFilter" small color="primary">
              <v-icon>mdi-plus</v-icon></v-btn
            >
          </v-col>
          <v-col>
            <v-switch
              v-model="field.multiValue"
              v-bind:label="$t('Multivalue')"
              @change="multiValue"
            ></v-switch>
          </v-col>
          <v-col cols="5">
            <div class="d-flex align-center">
              <v-autocomplete
                :loading="loading"
                v-model="field.default"
                :multiple="field.multiValue"
                v-bind:label="$t('DefaultValue')"
                item-text="text"
                item-value="id"
                :items="defaultValuesOptions"
                clearable
                :disabled="defaultValuesDisabled"
              >
                <template v-slot:append-outer>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn :disabled="defaultValuesDisabled" small icon @click="getDefaultValues()" v-on="on">
                        <v-icon>mdi-database-check</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t("Site.ContentType.InternalExternalTablesFields.GetDefaultValues") }}</span>
                  </v-tooltip>
                </template>
              </v-autocomplete>

              <v-tooltip v-if="defaultValuesDisabled" top>
                <template v-slot:activator="{ on }">
                  <v-icon
                    small
                    color="warning"
                    class="ml-2"
                    v-on="on"
                  >
                    mdi-alert-circle
                  </v-icon>
                </template>
                <span>{{ $t("Site.ContentType.InternalExternalTablesFields.DefaultValuesDisabled") }}</span>
              </v-tooltip>
            </div>
          </v-col>
        </v-row>
        <v-row justify="end" class="align-center">
          <v-col cols="5">
            <v-select
              :loading="loading"
              :multiple="field.multiValue"
              v-bind:label="$t('Test')"
              item-text="text"
              item-value="id"
              :items="testItems"
              :ref="`test-${field.code}`"
              clearable
            >
              <template v-slot:append-outer>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn small icon @click="test" v-on="on">
                      <v-icon>mdi-database-check</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("Test") }}</span>
                </v-tooltip>
              </template>
            </v-select>
          </v-col>
        </v-row>
      </v-col>
    </v-card-text>
    <InternalTableModal
      :ref="internalTableModalKey"
      @on-item-saved="getTables"
    />
    <ListFieldParamsModal
      :ref="listFieldParamsModalKey"
      @on-saved="executeTestQuery"
    />
  </v-card>
</template>

<script>
import Utils from '@/ecm/common/Utils';
import InternalTableModal from '@/components/administration/internalTable/InternalTableModal.vue';
import ListFieldParamsModal from '@/components/administration/contentType/fields/ListFieldParamsModal';
import ContentTypeService from '@/services/ContentTypeService';
import InternalTableService from '@/services/InternalTableService';
import InternalTableFilter from '@/ecm/models/contentType/ContentTypeFieldInternalTableFilter';
import InternalTable from '@/ecm/models/contentType/ContentTypeFieldInternalTable';
import { codemirror } from 'vue-codemirror';
// import base style
import 'codemirror/lib/codemirror.css';
import 'codemirror/mode/sql/sql';
import 'codemirror/theme/solarized.css';

export default {
    components: {
        codemirror,
        InternalTableModal,
        ListFieldParamsModal,
    },
    props: {
        field: {
            type: Object,
            default: () => ({}),
        },
        contentTypeFields: {
            type: Array,
            default: () => [],
        },
        validator: {
            type: Object,
            default: () => ({}),
        }
    },
    async created() {
        this.testItems = [];
        this.defaultValuesOptions = [];
        this.internalTables = await InternalTableService.getList();
        if (this.field.dynamicTableId && (parseInt(this.field.dynamicTableId) > 0 || parseInt(this.field.dynamicTableId) == -1)) {
            await this.loadColumns();
        }
        this.checkDefaultValuesDisabled();
        if (!this.defaultValuesDisabled) await this.getDefaultValues();
    },
    data() {
        return {
            internalTables: [],
            loading: false,
            testItems: [],
            columns: [],
            basicMode: true,
            internalTableModalKey: 'internalTableModalKey',
            listFieldParamsModalKey: 'listFieldParamsModalKey',
            cmOption: {
                tabSize: 4,
                styleActiveLine: true,
                lineNumbers: true,
                line: true,
                mode: 'text/x-mssql',
                theme: 'solarized light',
            },
            filters: [
                '##TOP##',
                '##LIKE##',
            ],
            selectedFilter: '',
            selectedTab: 0,
            defaultValuesOptions: [],
            defaultValuesDisabled: false
        };
    },
    computed: {
        internalTableModalInstance() {
            return this.$refs[this.internalTableModalKey];
        },
        dynamicTableIdErrors() {
            const errors = [];
            if (!this.validator.field.dynamicTableId.$dirty) return errors;
            !this.validator.field.dynamicTableId.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        }
    },

    watch: {
        'field.dynamicTableId': {
            immediate: true,
            async handler(val, oldVal) {
                this.testItems = [];
                if (parseInt(val) > 0) {
                    await this.loadColumns();
                }
            },
        },
        field: {
            immediate: false,
            async handler(val, oldVal) {
                this.checkDefaultValuesDisabled();
                if (!this.defaultValuesDisabled) await this.getDefaultValues(val.default);
                await this.loadColumns();
            },
        }
    },
    methods: {
        async getTables() {
            this.internalTables = await InternalTableService.getList();
            if (this.field.dynamicTableId && parseInt(this.field.dynamicTableId) > 0) {
                await this.loadColumns();
            }

        },
        onInternalTableButtonClick() {
            this.internalTableModalInstance.new();
        },
        addFilter() {
            this.field.internalTable.filters.push(new InternalTableFilter());
        },
        addPerformanceQueryFilter() {
            if (this.selectedTab == 1)
                this.field.performanceQuery = this.field.performanceQuery ? this.field.performanceQuery + this.selectedFilter : this.selectedFilter;
            else
                this.field.noResultPerformanceQuery = this.field.noResultPerformanceQuery ? this.field.noResultPerformanceQuery + this.selectedFilter : this.selectedFilter;
        },
        removeFilter(index) {
            this.field.internalTable.filters.splice(index, 1);
            if (this.field.internalTable.filters.length == 0) {
                this.addFilter();
            }
            this.generateCommand();
        },
        showAddFilter(index) {
            return index === this.field.internalTable.filters.length - 1;
        },
        async loadColumns() {
            const data = await InternalTableService.get(this.field.dynamicTableId);
            this.columns = data.Columns.map(function (column) {
                return column.Name;
            });
            this.basicMode = true;
        },
        async selectedTableChanged() {
            this.field.internalTable = new InternalTable();
            this.field.default = '';
            await this.loadColumns();
        },
        generateCommand() {
            if (!this.basicMode) return;

            if (this.field.internalTable.displayColumns.length == 0) {
                return;
            }

            let columns = '';

            for (let i = 0; i < this.field.internalTable.displayColumns.length; i++) {
                if (i == 0) {
                    columns += '[' + this.field.internalTable.displayColumns[i] + ']';
                } else {
                    columns +=
                        " + ' - ' + [" + this.field.internalTable.displayColumns[i] + ']';
                }
            }

            const tableName = this.internalTables.filter(
                (f) => f.id == this.field.dynamicTableId
            )[0].text;

            let query = `SELECT ${columns} AS VALUE FROM [${tableName}] `;

            for (let i = 0; i < this.field.internalTable.filters.length; i++) {
                if (
                    this.field.internalTable.filters[i].filterColumn &&
                    this.field.internalTable.filters[i].filterValue
                ) {
                    if (i == 0) {
                        query += ` WHERE [${this.field.internalTable.filters[i].filterColumn}] = '{${this.field.internalTable.filters[i].filterValue}}' `;
                    } else {
                        query += ` AND [${this.field.internalTable.filters[i].filterColumn}] = '{${this.field.internalTable.filters[i].filterValue}}' `;
                    }
                }
            }

            if (this.field.internalTable.sortColumn) {
                query += ` ORDER BY [${this.field.internalTable.sortColumn}]`;
            }

            this.field.command = query;
            this.queryHasChanged();
        },
        test() {
            this.testItems = [];
            this.generateCommand();
            var paramItems = this.contentTypeFields
                .filter((f) => this.field.command.includes(`{${f.code}}`))
                .map((f) => {
                    return {
                        code: f.code,
                        name: f.name,
                        multiValue: f.multiValue,
                        value: '',
                    };
                });

            if ((this.selectedTab === 1 && this.field.performanceQuery && this.field.performanceQuery.includes('##LIKE##')) ||
                (this.selectedTab === 3 && this.field.noResultPerformanceQuery && this.field.noResultPerformanceQuery.includes('##LIKE##'))) {
                paramItems.push({
                    code: '##LIKE##',
                    name: 'LIKE',
                    multiValue: false,
                    value: '',
                });
            }

            if (paramItems.length > 0) {
                this.$refs[this.listFieldParamsModalKey].edit(paramItems);
            } else {
                this.executeTestQuery(paramItems, true);
            }
        },
        async executeTestQuery(paramItems) {
            if (this.selectedTab === 1 && (!this.field.performanceQuery || this.filters.every(filter => !this.field.performanceQuery.includes(filter)))) {
                Utils.toast.error(this.$t('Site.ContentType.InternalExternalTablesFields.ErrorInPerformanceQuery'));
                return;
            } else if (this.selectedTab === 3 && (!this.field.noResultPerformanceQuery || this.filters.every(filter => !this.field.noResultPerformanceQuery.includes(filter)))) {
                Utils.toast.error(this.$t('Site.ContentType.InternalExternalTablesFields.ErrorInPerformanceQuery'));
                return;
            }

            const test = {
                Query: this.selectedTab === 0 ? this.field.command : '',
                PerformanceQuery: this.selectedTab === 1 ? this.field.performanceQuery : '',
                NoResultQuery: this.selectedTab === 2 ? this.field.noResultQuery : '',
                NoResultPerformanceQuery: this.selectedTab === 3 ? this.field.noResultPerformanceQuery : '',
                InternaTableId: this.field.dynamicTableId,
                QueryToExecute: this.selectedTab + 1,
                Parameters: paramItems.map((p) => {
                    return {
                        Name: p.code,
                        Value: p.value,
                        Multivalue: p.multiValue,
                    };
                }),
            };

            this.loading = true;
            ContentTypeService.testDynamicList(test)
                .then((data) => {
                    this.testItems = data;
                    Utils.toast.success(this.$t('SuccessfulTest'));
                })
                .catch(() => {
                    // error
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        async getDefaultValues(defaultValue) {
            if (this.field.id === 0 && (!this.field.command || this.field.dynamicTableId === '0')) return;

            this.defaultValuesOptions = [];
            const test = {
                Query: this.field.command ? this.field.command : '',
                NoResultQuery: this.field.noResultQuery ? this.field.noResultQuery : '',
                InternaTableId: this.field.dynamicTableId,
                QueryToExecute: this.getQueryToExecute(),
                Parameters: [],
            };

            this.loading = true;
            try {
                this.defaultValuesOptions = await ContentTypeService.testDynamicList(test);
                this.defaultValuesOptions = this.defaultValuesOptions.filter((f) => f.text !== '');
                if (defaultValue)
                    this.field.default = defaultValue;
                this.validateDefaultValue();
            } catch (error) {
                // error
            } finally {
                this.loading = false;
            }
        },
        validateDefaultValue() {
            const values = Array.isArray(this.field.default) ? this.field.default : this.field.default.split(',');
            const validValues = values.filter(value =>
                this.defaultValuesOptions.some(option => option.text === value)
            );
            this.field.default = this.field.multiValue ? validValues : validValues.join(',');
        },
        multiValue() {
            if (this.field.default) {
                if (!Array.isArray(this.field.default)) {
                    this.field.default = this.field.default.split(',');
                } else {
                    this.field.default = this.field.default[0];
                }
            }
        },
        queryHasChanged() {
            this.field.default = '';
            this.defaultValuesOptions = [];
            this.checkDefaultValuesDisabled();
        },
        getQueryToExecute() {
            if (this.field.command) return 1;
            return 3;
        },
        hasFilters() {
            const filters = this.field.internalTable.filters || [];
            const hasMultipleFilters = filters.length > 1;
            const hasNonEmptyFilter = filters.some(f =>
                (f.filterColumn && f.filterColumn.toString().trim() !== '') ||
                (f.filterValue && f.filterValue.toString().trim() !== '')
            );
            const commandContainsBracket = this.field.command && this.field.command.includes('{');
            const noResultQueryContainsBracket = this.field.noResultQuery && this.field.noResultQuery.includes('{');
            return !!(hasMultipleFilters || hasNonEmptyFilter || commandContainsBracket || noResultQueryContainsBracket);
        },
        checkDefaultValuesDisabled() {
            const hasPerformanceQuery = !!this.field.performanceQuery || !!this.field.noResultPerformanceQuery;
            this.defaultValuesDisabled = hasPerformanceQuery || this.hasFilters();
        }
    },
};
</script>

<style lang="scss">
.sql-editor {
  .CodeMirror {
    border: 1px solid #eee;
    height: auto !important;
    min-height: 200px !important;
    /*max-height: 500px;*/
    line-height: 20px;
  }
  .CodeMirror-scroll {
    min-height: 200px;
  }
}
</style>
